import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReCAPTCHA from "react-google-recaptcha";
import { Modal, Toast } from "../../Components";
import { motion } from "framer-motion";
import { accountService } from "../../Services";

const PASSWORD_REGEX = /^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{8,}$/;

export default function ModalPricingformCreate(props) {
  const [searchParams] = useSearchParams();

  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [error, setError] = useState({});
  const [captcha, setCaptcha] = useState("temp-bypass");
  const [isChecked, setIsChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSignupWithEmail, setIsSignupWithEmail] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [toastList, setToastList] = useState([]);

  const referId = searchParams.get("refId") || 0;
  const captchaKey = process.env.REACT_APP_CAPTCHA_KEY;
  const navigate = useNavigate();

  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href =
      "https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css";
    link.crossOrigin = "anonymous";
    link.referrerPolicy = "no-referrer";
    document.head.appendChild(link);

    return () => {
      document.head.removeChild(link);
    };
  }, []);

  const updateForm = (data) => setForm((prev) => ({ ...prev, ...data }));
  const setInput = (key) => (event) =>
    updateForm({ [key]: event.target.value });

  const validateForm = () => {
    const newErrors = {};
    if (!form.firstName.trim()) newErrors.firstName = "First name is required";
    if (!form.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!form.email.trim()) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(form.email))
      newErrors.email = "Invalid email format";
    if (!form.password) newErrors.password = "Password is required";
    else if (!PASSWORD_REGEX.test(form.password)) {
      newErrors.password =
        "Min 8 chars with uppercase, lowercase, number & special char";
    }
    if (form.password !== form.confirmPassword)
      newErrors.confirmPassword = "Passwords don't match";
    // if (!captcha) newErrors.captcha = "Please complete the captcha"; // Temporarily disabled
    if (!isChecked) newErrors.terms = "You must accept the terms";

    setError(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const createValidation = () => {
    setErrorMessage("");
    if (!validateForm()) return;

    setLoading(true);
    const payload = {
      title: "",
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
      password: form.password,
      confirmPassword: form.confirmPassword,
      refId: referId,
      role: "advertiser",
      acceptTerms: isChecked,
      captcha: captcha,
      subsProductId: props.subsProductId,
    };

    accountService
      .register(payload)
      .then((ressignup) => {
        if (ressignup.data.paypalurl && ressignup.data.paypalurl !== "") {
          accountService
            .login(form.email, form.password)
            .then((res) => {
              window.location.replace(ressignup.data.paypalurl);
            })
            .catch((loginErr) => {
              let errorMsg = String(loginErr).replace(/<\/?[^>]+(>|$)/gi, "");
              try {
                const parsed = JSON.parse(errorMsg);
                errorMsg = parsed.message || errorMsg;
              } catch (e) {}
              setToastList([
                ...toastList,
                { error: true, msg: errorMsg, id: Date.now() },
              ]);
            });
        } else {
          navigate("/account/email-verification", {
            state: { id: ressignup.data.id, showSuccessToast: true },
          });
        }
      })
      .catch((err) => {
        let errorMsg = String(err).replace(/<\/?[^>]+(>|$)/gi, "");
        try {
          const parsed = JSON.parse(errorMsg);
          errorMsg = parsed.message || errorMsg;
        } catch (e) {}

        // Check if error is about email already exists
        if (
          errorMsg.toLowerCase().includes("email") ||
          errorMsg.toLowerCase().includes("already")
        ) {
          setError({ ...error, email: errorMsg });
        } else {
          setToastList([
            ...toastList,
            { error: true, msg: errorMsg, id: Date.now() },
          ]);
        }
      })
      .finally(() => setLoading(false));
  };

  const handleLoginWithGoogle = () => {
    if (!isChecked) {
      setError({ terms: "You must accept the terms" });
      return;
    }
    props.setRoleId("I'm a brand");
    props.setReferId(referId);
    props.setModal("pricingPlatformCreate");
  };

  const handleSignInClick = () => {
    props.setModal("pricingsignin");
  };

  // Icons
  const GoogleIcon = () => (
    <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24">
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );

  const EmailIcon = () => (
    <svg
      className="w-5 h-5 sm:w-6 sm:h-6"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <rect
        x="2"
        y="4"
        width="20"
        height="16"
        rx="2"
        className="stroke-gray-600"
      />
      <path d="M22 6L12 13L2 6" className="stroke-gray-600" />
    </svg>
  );

  // Logo
  const Logo = () => (
    <div className="flex items-center gap-2">
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg"
        style={{
          background:
            "linear-gradient(135deg, #10b981 0%, #059669 50%, #047857 100%)",
          boxShadow: "0 4px 16px rgba(16, 185, 129, 0.3)",
        }}
      >
        <img
          src={process.env.PUBLIC_URL + "/favicon/favicon.ico"}
          className="h-5 w-5 drop-shadow-md"
          alt="Logo"
        />
      </div>
      <span
        className="font-bold text-xl tracking-wide"
        style={{
          background: "linear-gradient(90deg, #059669, #10b981, #34d399)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        TAMA
      </span>
    </div>
  );

  return (
    <Modal
      addClass="platform pricingModal"
      close={props.close}
      className="top-2 right-16"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-2xl w-full max-w-[420px] max-h-[90vh] overflow-y-auto p-4 sm:p-6"
        style={{
          scrollbarWidth: "thin",
          scrollbarColor: "#d1d5db transparent",
        }}
      >
        {/* Logo */}
        <div className="flex items-center justify-center mb-4">
          <Logo />
        </div>

        {/* Account Type Badge */}
        <div className="flex items-center justify-center gap-2 mb-4 p-2 rounded-lg bg-gradient-to-r from-purple-50 to-indigo-50">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
            <i className="fas fa-building text-white text-xs"></i>
          </div>
          <span className="text-sm font-medium text-gray-700">
            Brand Account
          </span>
        </div>

        {isSignupWithEmail ? (
          /* Email Signup Form */
          <>
            <button
              onClick={() => setIsSignupWithEmail(false)}
              className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-700 mb-3 transition-colors"
            >
              <i className="fas fa-arrow-left"></i>
              Back
            </button>

            <h1 className="text-lg font-semibold text-gray-900 mb-1 text-center">
              Create Brand Account
            </h1>
            <p className="text-gray-500 text-xs mb-4 text-center">
              Start finding influencers for your campaigns
            </p>

            {/* Name Row */}
            <div className="flex gap-2 mb-3">
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  placeholder="First name"
                  value={form.firstName}
                  onChange={setInput("firstName")}
                  className={`w-full px-3 py-2 text-sm border rounded-lg outline-none transition-all bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 ${
                    error.firstName ? "border-red-500" : "border-gray-200"
                  }`}
                  autoFocus
                />
                {error.firstName && (
                  <span className="text-red-500 text-xs mt-0.5 block">
                    {error.firstName}
                  </span>
                )}
              </div>
              <div className="flex-1">
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Last Name
                </label>
                <input
                  type="text"
                  placeholder="Last name"
                  value={form.lastName}
                  onChange={setInput("lastName")}
                  className={`w-full px-3 py-2 text-sm border rounded-lg outline-none transition-all bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 ${
                    error.lastName ? "border-red-500" : "border-gray-200"
                  }`}
                />
                {error.lastName && (
                  <span className="text-red-500 text-xs mt-0.5 block">
                    {error.lastName}
                  </span>
                )}
              </div>
            </div>

            {/* Email */}
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                placeholder="Your email address"
                value={form.email}
                onChange={setInput("email")}
                className={`w-full px-3 py-2 text-sm border rounded-lg outline-none transition-all bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 ${
                  error.email ? "border-red-500" : "border-gray-200"
                }`}
              />
              {error.email && (
                <span className="text-red-500 text-xs mt-0.5 block">
                  {error.email}
                </span>
              )}
            </div>

            {/* Password */}
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Create a password"
                  value={form.password}
                  onChange={setInput("password")}
                  className={`w-full px-3 py-2 pr-10 text-sm border rounded-lg outline-none transition-all bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 ${
                    error.password ? "border-red-500" : "border-gray-200"
                  }`}
                />
                <i
                  className={`fas ${
                    showPassword ? "fa-eye" : "fa-eye-slash"
                  } absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-600 text-sm`}
                  onClick={() => setShowPassword(!showPassword)}
                ></i>
              </div>
              {error.password && (
                <span className="text-red-500 text-xs mt-0.5 block">
                  {error.password}
                </span>
              )}
            </div>

            {/* Confirm Password */}
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm your password"
                  value={form.confirmPassword}
                  onChange={setInput("confirmPassword")}
                  className={`w-full px-3 py-2 pr-10 text-sm border rounded-lg outline-none transition-all bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 ${
                    error.confirmPassword ? "border-red-500" : "border-gray-200"
                  }`}
                />
                <i
                  className={`fas ${
                    showConfirmPassword ? "fa-eye" : "fa-eye-slash"
                  } absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-gray-600 text-sm`}
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                ></i>
              </div>
              {error.confirmPassword && (
                <span className="text-red-500 text-xs mt-0.5 block">
                  {error.confirmPassword}
                </span>
              )}
            </div>

            {/* Captcha */}
            <div className="mb-3 flex justify-center transform scale-[0.85] origin-center">
              <ReCAPTCHA sitekey={captchaKey} onChange={setCaptcha} />
            </div>
            {error.captcha && (
              <span className="text-red-500 text-xs block text-center mb-2">
                {error.captcha}
              </span>
            )}

            {/* Terms */}
            <div className="mb-3">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => setIsChecked(!isChecked)}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 text-indigo-500 focus:ring-indigo-500"
                />
                <span className="text-xs text-gray-600">
                  I agree to the{" "}
                  <button
                    type="button"
                    onClick={() => props.setSubModal("terms")}
                    className="text-indigo-500 hover:underline"
                  >
                    Terms & Conditions
                  </button>
                </span>
              </label>
              {error.terms && (
                <span className="text-red-500 text-xs mt-0.5 block">
                  {error.terms}
                </span>
              )}
            </div>

            {/* Error Message */}
            {errorMessage && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-2 mb-3">
                <p className="text-xs text-red-600">{errorMessage}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              onClick={createValidation}
              disabled={loading}
              className={`w-full py-2.5 text-sm font-semibold text-white rounded-full transition-all duration-200 mb-3 ${
                loading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-gray-900 hover:bg-gray-800 active:scale-[0.98]"
              }`}
            >
              {loading ? "CREATING ACCOUNT..." : "CREATE ACCOUNT"}
            </button>
          </>
        ) : (
          /* Social Signup Options */
          <>
            <h1 className="text-lg font-semibold text-gray-900 mb-1 text-center">
              Create Brand Account
            </h1>
            <p className="text-gray-600 mb-4 text-xs text-center">
              Choose how you want to sign up
            </p>

            {/* Terms Checkbox */}
            <div className="mb-4">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => setIsChecked(!isChecked)}
                  className="mt-0.5 w-4 h-4 rounded border-gray-300 text-indigo-500 focus:ring-indigo-500"
                />
                <span className="text-xs text-gray-600">
                  I agree to the{" "}
                  <button
                    type="button"
                    onClick={() => props.setSubModal("terms")}
                    className="text-indigo-500 hover:underline"
                  >
                    Terms & Conditions
                  </button>
                </span>
              </label>
              {error.terms && (
                <span className="text-red-500 text-xs mt-0.5 block">
                  {error.terms}
                </span>
              )}
            </div>

            {/* Social Buttons */}
            <div className="flex flex-col gap-2">
              <button
                onClick={handleLoginWithGoogle}
                className="w-full py-2.5 text-sm font-medium bg-white border border-gray-200 rounded-lg flex items-center justify-center gap-2 transition-all hover:bg-gray-50 hover:border-gray-300"
              >
                <GoogleIcon />
                Sign up with Google
              </button>

              <button
                onClick={() => setIsSignupWithEmail(true)}
                className="w-full py-2.5 text-sm font-medium bg-white border border-gray-200 rounded-lg flex items-center justify-center gap-2 transition-all hover:bg-gray-50 hover:border-gray-300"
              >
                <EmailIcon />
                Sign up with Email
              </button>
            </div>
          </>
        )}

        {/* Sign In Link */}
        <p className="text-center text-xs text-gray-500 mt-4">
          Already have an account?{" "}
          <button
            onClick={handleSignInClick}
            className="text-indigo-500 font-medium hover:text-indigo-600"
          >
            Sign In
          </button>
        </p>

        {/* Footer */}
        <div className="border-t border-gray-100 mt-4 pt-3">
          <div className="flex items-center justify-center gap-4 text-xs text-gray-500">
            <span>© 2026</span>
            <a
              href="https://tamayou.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-gray-700"
            >
              Privacy
            </a>
            <a
              href="https://tamayou.com/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-gray-700"
            >
              Terms
            </a>
          </div>
        </div>
      </motion.div>
      {toastList.length > 0 && (
        <Toast list={toastList} setList={setToastList} />
      )}
    </Modal>
  );
}
